import { Loader2, Save } from "lucide-react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { consentFormSchema, ConsentFormValues, mapApiUserToFormValues } from "../../lib/consent-schema";
import type { UserListItem } from "../../lib/types";
import { ConsentFormFields } from "./ConsentFormFields";

interface UserEditFormProps {
  user: UserListItem;
  onSave: (data: ConsentFormValues) => void;
  onCancel: () => void;
}

export function UserEditForm({
  user,
  onSave,
  onCancel,
}: UserEditFormProps) {
  const {
    register,
    handleSubmit,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<ConsentFormValues>({
    resolver: zodResolver(consentFormSchema),
    defaultValues: {
      ...mapApiUserToFormValues(user),
      firstName: user.firstName,
      lastName: user.lastName,
      email: user.email || "",
      consentFormParentCarerFirstName:
        user.consentFormParentCarerFirstName || "",
      consentFormParentCarerSurname: user.consentFormParentCarerSurname || "",
      consentFormParentCarerAddress: user.consentFormParentCarerAddress || "",
      consentFormParentCarerRelationship:
        user.consentFormParentCarerRelationship || "",
      consentFormParentCarerContactNumber:
        user.consentFormParentCarerContactNumber || "",
      consentFormEmergencyContact1Name:
        user.consentFormEmergencyContact1Name || "",
      consentFormEmergencyContact1Relationship:
        user.consentFormEmergencyContact1Relationship || "",
      consentFormEmergencyContact1Phone:
        user.consentFormEmergencyContact1Phone || "",
      consentFormEmergencyContact2Name:
        user.consentFormEmergencyContact2Name || "",
      consentFormEmergencyContact2Relationship:
        user.consentFormEmergencyContact2Relationship || "",
      consentFormEmergencyContact2Phone:
        user.consentFormEmergencyContact2Phone || "",
      consentFormImportantMedicalInformation:
        user.consentFormImportantMedicalInformation || "",
      consentFormMedicationDetails: user.consentFormMedicationDetails || "",
      consentFormInjuriesDetails: user.consentFormInjuriesDetails || "",
      consentFormDietaryRequirementsDetails:
        user.consentFormDietaryRequirementsDetails || "",
      consentFormDisabilityDetails: user.consentFormDisabilityDetails || "",
      dataProcessingConsent: !!user.dataProcessingConsent,
      emergencyTreatmentConsent: !!user.emergencyTreatmentConsent,
      photoConsent:
        user.photoConsent === null
          ? undefined
          : user.photoConsent?.toString(),
      isUnder18: !!user.consentFormParticipantDateOfBirth,
      hasMedication: !!user.consentFormMedicationDetails,
      hasInjuries: !!user.consentFormInjuriesDetails,
      hasDietaryRequirements: !!user.consentFormDietaryRequirementsDetails,
      hasDisability: !!user.consentFormDisabilityDetails,
    },
  });

  return (
    <form onSubmit={handleSubmit(onSave)} className="w-full space-y-8">
      <div className="bg-gray-50 p-6 rounded-lg border-2 border-primary-100">
        <div className="flex justify-between items-center mb-6">
          <h4 className="text-xl font-bold text-gray-900">
            Editing {user.firstName} {user.lastName}
          </h4>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={onCancel}
              className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="inline-flex items-center px-6 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50"
            >
              {isSubmitting ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <Save className="w-4 h-4 mr-2" />
              )}
              Save And Update Consent
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-2 mb-8 pb-8 border-b border-gray-200">
          <div>
            <label className="block text-sm font-medium text-gray-700">
              First Name
            </label>
            <input
              {...register("firstName")}
              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
            />
            {errors.firstName && (
              <p className="mt-1 text-sm text-red-600">
                {errors.firstName.message}
              </p>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">
              Last Name
            </label>
            <input
              {...register("lastName")}
              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
            />
            {errors.lastName && (
              <p className="mt-1 text-sm text-red-600">
                {errors.lastName.message}
              </p>
            )}
          </div>
          <div className="sm:col-span-2">
            <label className="block text-sm font-medium text-gray-700">
              Email
            </label>
            <input
              {...register("email")}
              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
            />
            {errors.email && (
              <p className="mt-1 text-sm text-red-600">{errors.email.message}</p>
            )}
          </div>
        </div>

        <div className="space-y-6">
          <h5 className="text-lg font-bold text-gray-900">
            User Consents & Information
          </h5>
          <ConsentFormFields
            register={register}
            errors={errors}
            watch={watch}
            name={`${user.firstName} ${user.lastName}`}
            user={user}
          />
        </div>
      </div>
    </form>
  );
}


